import { type ImageFormValue } from '@/api/common/common.models';
import { MAX_TOTAL_IMAGES_SIZE_MB } from '@/api/costs/costs.models';

export function getTotalImagesSize(images: ImageFormValue[]): number {
    return images.reduce(
        (sum, img) => sum + (img.imageFile?.size ?? img.sizeBytes ?? 0),
        0,
    );
}

export function formatImagesSizeLabel(images: ImageFormValue[]): string {
    const totalMB = getTotalImagesSize(images) / (1024 * 1024);
    return `Images (${totalMB.toFixed(1)} of ${MAX_TOTAL_IMAGES_SIZE_MB} MB)`;
}

const MAX_IMAGE_DIMENSION = 2048;

// These types below do not affect input types, any weird image input type will get converted to jpeg anyways.
const SUPPORTED_OUTPUT_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
]);

function getOutputType(mimeType: string): string {
    return SUPPORTED_OUTPUT_TYPES.has(mimeType) ? mimeType : 'image/jpeg';
}

function replaceExtension(fileName: string, mimeType: string): string {
    const extMap: Record<string, string> = {
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/webp': '.webp',
    };
    const newExt = extMap[mimeType];
    if (!newExt) return fileName;

    const lastDot = fileName.lastIndexOf('.');
    const base = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
    return `${base}${newExt}`;
}

export async function resizeImage(file: File): Promise<File> {
    if (
        !file.type.startsWith('image/') ||
        !('createImageBitmap' in globalThis) ||
        !('OffscreenCanvas' in globalThis)
    ) {
        return file;
    }

    try {
        const bitmap = await createImageBitmap(file);
        const { width, height } = bitmap;

        if (width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION) {
            bitmap.close();
            return file;
        }

        const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
        const newWidth = Math.round(width * scale);
        const newHeight = Math.round(height * scale);

        const canvas = new OffscreenCanvas(newWidth, newHeight);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
        bitmap.close();

        const outputType = getOutputType(file.type);
        const blob = await canvas.convertToBlob({ type: outputType });
        const outputName = replaceExtension(file.name, outputType);

        return new File([blob], outputName, {
            type: outputType,
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    }
}

export const sanitizeFileName = (file: File): File => {
    const name = file.name || 'file';
    const lastDotIndex = name.lastIndexOf('.');

    const base = lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name;
    const extension = lastDotIndex > 0 ? name.slice(lastDotIndex + 1) : '';

    const stripDiacritics = (value: string) =>
        value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

    const safeBase =
        stripDiacritics(base)
            .replace(/[^a-zA-Z0-9_-]+/g, '-')
            .replace(/-{2,}/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 64) || 'file';

    const safeExtension = stripDiacritics(extension)
        .replace(/[^a-zA-Z0-9]+/g, '')
        .toLowerCase();

    const safeName = safeExtension ? `${safeBase}.${safeExtension}` : safeBase;

    return new File([file], safeName, {
        type: file.type,
        lastModified: file.lastModified,
    });
};
